import { Injectable, Injector, Type } from '@angular/core';
import { NodeType } from 'src/app/shared/enums/node-type';
import { ParagraphComponent } from './nodes';
import { HeadingComponent } from './nodes';
import { LinkComponent } from './nodes';
import { BulletListComponent } from './nodes';
import { NumberedListComponent } from './nodes';
import { SectionComponent } from './nodes/section/section.component';
import { TableOfContentsComponent } from './nodes/table-of-contents/table-of-contents.component';
import { AuthorComponent } from './nodes/author/author.component';
import { ReviewerComponent } from './nodes/reviewer/reviewer.component';
import { ApproverComponent } from './nodes/approver/approver.component';
import { CpSelectorComponent } from './nodes/cp-selector/cp-selector.component';
import { AppendixComponent } from './nodes/appendix/appendix.component';
import { TestSectionComponent } from './nodes/test-section/test-section.component';
import { CheckTestComponent } from './nodes/check-test/check-test.component';

@Injectable({
  providedIn: 'root',
})
export class NodeFactoryService {
  // Cache injectors by node id so we don't create a new Injector object on every
  // change-detection pass (which would cause ngComponentOutlet to recreate
  // components repeatedly). Keys are node id (number or string).
  private injectorCache: Map<number | string, Injector> = new Map();
  // Track last node object reference for each key so we can refresh injectors when immutable clones replace nodes
  private nodeRefCache: Map<number | string, any> = new Map();

  /** Clear cached injectors (call when the document is replaced) */
  clearCache(): void {
    this.injectorCache.clear();
    this.nodeRefCache.clear();
  }
  private nodeComponentMap: { [key in NodeType]?: Type<any> } = {
    [NodeType.PARAGRAPH]: ParagraphComponent,
    [NodeType.HEADING]: HeadingComponent,
    [NodeType.LINK]: LinkComponent,
    [NodeType.BULLETED_LIST]: BulletListComponent,
    [NodeType.NUMBERED_LIST]: NumberedListComponent,
    [NodeType.SECTION]: SectionComponent,
    [NodeType.TABLE_OF_CONTENTS]: TableOfContentsComponent,
    [NodeType.AUTHOR]: AuthorComponent,
    [NodeType.REVIEWER]: ReviewerComponent,
    [NodeType.APPROVER]: ApproverComponent,
    [NodeType.CP_SELECTOR]: CpSelectorComponent,
    [NodeType.APPENDIX]: AppendixComponent,
    [NodeType.TEST_SECTION]: TestSectionComponent,
    [NodeType.PROCEDURE_CHECK]: CheckTestComponent,
  };

  getComponentForNodeType(nodeType: NodeType): Type<any> | null {
    return this.nodeComponentMap[nodeType] || null;
  }

  createInjector(node: any, documentNode: any, injector: Injector): Injector {
    // Use node.id when possible to keep the injector reference stable even if
    // the surrounding document object is cloned. Fall back to a string key
    // derived from the node if no id is present.
    const key = node && node.id !== undefined && node.id !== null ? node.id : node && node.name ? String(node.name) : JSON.stringify(node);
    const cached = this.injectorCache.get(key);
    const prevRef = this.nodeRefCache.get(key);

    // If we have a cached injector AND the node reference is unchanged, reuse it
    if (cached && prevRef === node) {
      return cached;
    }

    // Otherwise create (or refresh) injector with the new node reference
    const created = Injector.create({
      providers: [
        { provide: 'documentNode', useValue: node },
        { provide: 'node', useValue: documentNode },
      ],
      parent: injector,
    });
    this.injectorCache.set(key, created);
    this.nodeRefCache.set(key, node);
    return created;
  }
}
